import { useMemo, useState } from 'react'
import type { JSX } from 'react'

import { ChannelAvatar, Flex, Table, Text } from '@buffer-mono/popcorn'

import { FilterInput } from '~publish/components/FilterInput'

import {
  ACCESS_LEVEL_OPTIONS,
  COMMUNITY_TOOLTIP,
  ProductHeaderLabel,
  PUBLISH_TOOLTIP,
} from '../../Team/components/ChannelPermissions'
import type { AccessLevel, ProductKey } from '../../Team/types'
import type { ChannelWithProductAccess } from '../hooks/useMyChannelPermissions'

import styles from './ChannelPermissionsTable.module.css'

// Above this many channels, show a search box to filter the list.
// Smaller lists are easy to scan, so the input would just be noise.
const CHANNEL_SEARCH_THRESHOLD = 10

/**
 * Read-only icon + label for one product's access level, reusing the option
 * set (labels, icons, colors) from the admin-facing access-level selects so
 * both surfaces always describe a level the same way.
 */
const AccessLevelDisplay = ({
  product,
  level,
}: {
  product: ProductKey
  level: AccessLevel
}): JSX.Element | null => {
  // The publish list is the superset of every level, so it backstops a level
  // outside the product's own option set (Community has no `requiresApproval`).
  const option =
    ACCESS_LEVEL_OPTIONS[product].find(({ value }) => value === level) ??
    ACCESS_LEVEL_OPTIONS.publish.find(({ value }) => value === level)

  if (!option) {
    return null
  }

  return (
    <Flex align="center" gap="xs">
      <span className={`${styles.levelIcon} ${option.colorClass}`}>
        {option.icon}
      </span>
      <Text weight="medium">{option.label}</Text>
    </Flex>
  )
}

interface ChannelPermissionsTableProps {
  channels: ChannelWithProductAccess[]
}

/**
 * View-only per-channel permissions table for the current user: one row per
 * channel they can see, with their Publish and Community access levels.
 */
export const ChannelPermissionsTable = ({
  channels,
}: ChannelPermissionsTableProps): JSX.Element => {
  const [search, setSearch] = useState('')
  const showSearch = channels.length > CHANNEL_SEARCH_THRESHOLD

  const visibleChannels = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return channels
    return channels.filter(
      (channel) =>
        channel.name.toLowerCase().includes(term) ||
        channel.service.toLowerCase().includes(term),
    )
  }, [channels, search])

  return (
    <Flex direction="column" gap="xs" align="stretch" fullWidth>
      {showSearch && (
        <FilterInput
          value={search}
          onValueChange={setSearch}
          placeholder="Search channels"
          aria-label="Search channels"
        />
      )}
      <Table className={styles.permissionsTable}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell type="column">
              <Text weight="medium" color="default">
                Channels · {channels.length}
              </Text>
            </Table.HeaderCell>
            <Table.HeaderCell type="column" className={styles.productCol}>
              <ProductHeaderLabel label="Publish" tooltip={PUBLISH_TOOLTIP} />
            </Table.HeaderCell>
            <Table.HeaderCell type="column" className={styles.productCol}>
              <ProductHeaderLabel
                label="Community"
                tooltip={COMMUNITY_TOOLTIP}
              />
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {visibleChannels.map((channel) => (
            <Table.Row key={channel.id}>
              <Table.Cell className={styles.channelNameCell}>
                <Flex
                  align="center"
                  gap="sm"
                  className={styles.channelCell}
                  translate="no"
                >
                  <ChannelAvatar
                    src={channel.avatar}
                    alt={channel.name}
                    channel={channel.service}
                    size="small"
                  />
                  <Text className={styles.channelName} title={channel.name}>
                    {channel.name}
                  </Text>
                </Flex>
              </Table.Cell>
              <Table.Cell className={styles.productCol}>
                <AccessLevelDisplay
                  product="publish"
                  level={channel.productAccessLevels.publish}
                />
              </Table.Cell>
              <Table.Cell className={styles.productCol}>
                <AccessLevelDisplay
                  product="comments"
                  level={channel.productAccessLevels.comments}
                />
              </Table.Cell>
            </Table.Row>
          ))}
          {visibleChannels.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={3}>
                <Text color="subtle">No channels found</Text>
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </Flex>
  )
}
